// @flow

import * as React from 'react';
import { Button, Dialog, DialogTitle, Divider } from '@material-ui/core';
import type { Placement } from '@floating-ui/dom';
import { computePosition, autoUpdate, shift } from '@floating-ui/dom';
import { useResizeRect, useClickOutsideObserver } from '@realadvisor/observe';
import { Box } from 'react-system';
import { Portal } from '../popup';
import { useLocale } from '../locale';
import { FilterDialog } from './FilterDialog';
import { FilterItem, type Styles } from './FilterItem';
import { useFilterContext } from './filterContext';

type Props = {|
  empty?: boolean,
  label: React.Node,
  dialogTitle?: React.Node,
  onReset?: () => void,
  children: React.Node,
  Icon?: React.AbstractComponent<{| size: number |}>,
  open?: boolean,
  onOpenChange?: ((boolean => boolean) | boolean) => void,
  placement?: Placement,
  mode?: 'dialog' | 'popover',
  borderRadius?: number,
  paperMargin?: number,
  checkedFiltersVariant?: boolean,
  styles?: Styles,
  dialogActions?: React.Node, // will render default action if null
|};

const DEFAULT_COLOR = {
  main: '#ccc',
  active: '#fff',
};

const useStateOrProps = ([value, setValue], defaultvalue) => {
  const [localValue, localSetValue] = React.useState(defaultvalue);
  if (setValue != null && value != null) {
    return [value, setValue];
  }
  return [localValue, localSetValue];
};

export const Filter = ({
  dialogTitle,
  paperMargin,
  empty = true,
  onReset,
  label,
  color = DEFAULT_COLOR,
  placement = 'bottom-end',
  mode = 'popover',
  children,
  Icon,
  open: openProp,
  onOpenChange,
  borderRadius = 100,
  checkedFiltersVariant,
  dialogActions,
  styles,
}: Props): React.Node => {
  const { bg, chipBg } = useFilterContext();
  const { t } = useLocale();

  const [open, setOpen] = useStateOrProps([openProp, onOpenChange], false);
  const referenceRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const rect = useResizeRect(referenceRef);

  const closePopup = () => {
    setOpen(false);
  };

  useClickOutsideObserver([referenceRef, popupRef], closePopup);

  React.useEffect(() => {
    const reference = referenceRef.current;
    const popup = popupRef.current;
    if (open && reference != null && popup != null) {
      const update = () => {
        computePosition(reference, popup, {
          placement,
          strategy: 'fixed',
          middleware: [shift()],
        }).then(({ strategy, x, y }) => {
          popup.style.position = strategy;
          popup.style.left = `${x}px`;
          popup.style.top = `${y}px`;
        });
      };
      update();
      const cleanup = autoUpdate(reference, popup, update);
      return cleanup;
    }
  }, [open, placement]);

  return (
    <>
      <div ref={referenceRef}>
        <FilterItem
          label={label}
          active={open || !empty}
          open={open}
          onClick={() => {
            setOpen(!open);
          }}
          onDelete={e => {
            e.stopPropagation();
            if (!empty) {
              if (onReset != null) {
                onReset();
                setOpen(false);
              }
            } else {
              setOpen(!open);
            }
          }}
          bg={{ main: bg, active: chipBg }}
          color={color}
          styles={styles}
          empty={empty}
          hasCloseIcon={onReset != null}
          Icon={Icon}
          borderRadius={borderRadius}
          checkedFiltersVariant={checkedFiltersVariant}
        />
      </div>
      {mode === 'popover' && open && (
        <Portal>
          <div ref={popupRef} css={{ width: 'max-content', zIndex: 1299 }}>
            {rect && (
              <>
                <FilterDialog
                  dialogTitle={dialogTitle}
                  minWidth={rect.width}
                  paperMargin={paperMargin}
                  actions={
                    dialogActions ?? (
                      <>
                        <Divider />
                        <Box p={3}>
                          <Button size="small" onClick={closePopup}>
                            {t('save')}
                          </Button>
                        </Box>
                      </>
                    )
                  }
                >
                  {children}
                </FilterDialog>
              </>
            )}
          </div>
        </Portal>
      )}
      {mode === 'dialog' && (
        <Dialog open={open} onClose={closePopup}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {children}
        </Dialog>
      )}
    </>
  );
};
